import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { mappingTypeForOrder } from '../map/assignToRouteHelpers';

// Sum shipper + carrier revenue across a flat list of order objects, mirroring
// the per-order logic used for a route's revenue totals in RouteListItem.
// `includeShipperRates` gates shipper revenue behind the PLAN_PAGE_SHIPPER_RATES
// circle so the total matches the existing display behavior.
export const sumOrderRevenue = (orders, includeShipperRates) => {
    let carrierRevenue = 0;
    let shipperRevenue = 0;
    (orders || []).forEach((order) => {
        if (!order) return;
        if (order.oms) {
            carrierRevenue += order.order_revenue || 0;
        } else {
            shipperRevenue += order.shipper_rate || 0;
            carrierRevenue += order.carrier_rate || 0;
        }
    });
    return {
        carrierRevenue,
        shipperRevenue: includeShipperRates ? shipperRevenue : 0,
    };
};

// The route as it would be if `ordersToAdd` were assigned to it: their stops placed with the same
// helper the actual assignment uses (FTLStopHelpers.addOrders), so a preview matches what
// assignment produces — including merging an order into an existing stop when addresses coincide.
//
// addOrders throws for orders with missing/duplicate geocoding (the real assign flow catches this
// and notifies). This runs during render for a passive preview, so `placed` is false and the stops
// stay as they are rather than letting the card crash. The pending orders are mapped onto the route
// either way: isCustomerStop classifies a stop by looking its order ids up in route.orders.
export const projectRoute = (route, ordersToAdd) => {
    const pending = ordersToAdd || [];
    const orders = [
        ...(route?.orders || []),
        ...pending.map((order) => ({ order_id: order.order_id, type: mappingTypeForOrder(order), order })),
    ];

    try {
        return { route: { ...route, orders, stopsByRouteId: FTLStopHelpers.addOrders(route, pending) }, placed: true };
    } catch (e) {
        return { route: { ...route, orders, stopsByRouteId: route?.stopsByRouteId || [] }, placed: false };
    }
};

// Customer stop count the route would have if `ordersToAdd` were assigned to it. Orders that can't
// be placed add no stops.
export const computeProjectedCustomerStops = (route, ordersToAdd) => {
    const { route: projected, placed } = projectRoute(route, ordersToAdd);
    const counted = placed ? projected : route;

    return (counted?.stopsByRouteId || []).filter((stop) => FTLStopHelpers.isCustomerStop(stop, counted)).length;
};

// Projected route stats if `ordersToAdd` were assigned to `route`. Revenue is the
// sum over the route's current orders plus the pending ones; the customer stop
// count is re-derived from the projected stop sequence, so the preview matches
// what assignment will produce once committed. Pure — no side effects.
export const computeRoutePreviewStats = ({ route, existingOrders, ordersToAdd, includeShipperRates }) => {
    const revenue = sumOrderRevenue([...(existingOrders || []), ...(ordersToAdd || [])], includeShipperRates);
    const customerStops = computeProjectedCustomerStops(route, ordersToAdd);
    return {
        carrierRevenue: revenue.carrierRevenue,
        shipperRevenue: revenue.shipperRevenue,
        customerStops,
    };
};
